// Watabaga COOKING POT panel — a craft-station-style menu of the employment-gated cook recipes
// (Hog Burger). Mirrors the craft station look: input → output, a quantity stepper, a Cook button that
// starts the 10s channel. Only reachable while clocked in + standing at the pot (world3d gates it).

import { COOK_RECIPES, ITEM_NAMES, foodCost, type CookRecipe } from "@ghostland/shared";
import { injectTheme } from "./theme.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { foodMenuStore } from "../items/foodMenuStore.js";
import { drawItemIcon } from "../items/itemArt.js";

export interface CookActions {
  /** Start cooking `quantity` units of `recipeId` at the pot (server-authorised, 10s per unit). */
  cook: (recipeId: string, quantity: number) => void;
}
let actions: CookActions | null = null;
export function setCookActions(a: CookActions): void { actions = a; }

const STYLE_ID = "ghostland-cook-style";
const CSS = `
#gl-cook-overlay { position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center;
  background: rgba(5,0,7,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text); }
#gl-cook-overlay.open { display: flex; }
#gl-cook-card { width: 380px; max-width: 92vw; background: var(--gl-panel); border: 1px solid var(--gl-border);
  border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7); overflow: hidden; }
#gl-cook-head { padding: 16px 18px 12px; border-bottom: 1px solid var(--gl-border); }
#gl-cook-head h1 { margin: 0; font-size: 15px; letter-spacing: .16em; }
#gl-cook-head .hint { font-size: 11px; color: var(--gl-muted); margin-top: 4px; }
#gl-cook-body { padding: 14px 18px; }
.gl-cook-recipe { border: 1px solid var(--gl-border); border-radius: 9px; padding: 12px 14px; margin-bottom: 12px; background: var(--gl-panel-2); }
.gl-cook-title { font-size: 13px; margin-bottom: 4px; }
.gl-cook-title .t { font-size: 10px; color: var(--gl-muted); margin-left: 6px; }
.gl-cook-io { display: flex; align-items: center; gap: 10px; margin: 6px 0 12px; flex-wrap: wrap; }
.gl-cook-io .chip { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.gl-cook-io .chip canvas { width: 30px; height: 30px; image-rendering: pixelated; }
.gl-cook-io .chip .lack { color: #e06a6a; }
.gl-cook-io .arrow { color: var(--gl-muted); }
.gl-cook-qty { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 12px; }
.gl-cook-qty input { width: 60px; padding: 6px 8px; box-sizing: border-box; text-align: center;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 6px; color: var(--gl-text); font-family: inherit; font-size: 13px; }
.gl-cook-qty button { width: 26px; height: 26px; background: var(--gl-panel); border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-text); cursor: pointer; font-family: inherit; font-size: 14px; }
.gl-cook-qty button:hover { border-color: var(--gl-border-focus); }
.gl-cook-qty .max { color: var(--gl-muted); }
.gl-cook-go { width: 100%; padding: 9px 0; background: var(--gl-accent); color: var(--gl-accent-fg);
  border: 1px solid var(--gl-accent); border-radius: 7px; cursor: pointer; font-family: inherit; font-size: 13px; }
.gl-cook-go:hover:not(:disabled) { background: var(--gl-accent-hover); border-color: var(--gl-accent-hover); }
.gl-cook-go:disabled { opacity: .45; cursor: default; }
#gl-cook-close { margin-top: 10px; width: 100%; padding: 7px 0; background: none; border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-muted); cursor: pointer; font-family: inherit; font-size: 11px; }
`;

let overlay: HTMLDivElement | null = null;
let bodyEl: HTMLDivElement | null = null;
let unsub: Array<() => void> = [];

function ownedCount(itemId: string): number {
  return inventoryStore.get().reduce((n, s) => (s.itemId === itemId ? n + s.quantity : n), 0);
}
function affordable(recipe: CookRecipe): number {
  let min = Infinity;
  for (const ing of recipe.inputs) min = Math.min(min, Math.floor(ownedCount(ing.itemId) / ing.qty));
  return Number.isFinite(min) ? min : 0;
}
function iconFor(itemId: string): HTMLCanvasElement {
  const c = drawItemIcon(itemId); c.style.imageRendering = "pixelated"; return c;
}
/** The building's live wage for stocking this dish (owner-adjustable), else the catalogue default. */
function liveCost(itemId: string): number {
  const it = foodMenuStore.get().items.find((i) => i.itemId === itemId);
  return it ? it.cost : foodCost(itemId);
}

/** One recipe card: inputs→output, a quantity stepper, and a Cook button. */
function recipeCard(recipe: CookRecipe): HTMLDivElement {
  const max = affordable(recipe);
  const card = document.createElement("div"); card.className = "gl-cook-recipe";
  const secs = Math.round(recipe.durationMs / 1000);
  card.innerHTML = `<div class="gl-cook-title">${recipe.name} <span class="t">${secs}s each · +$${liveCost(recipe.output.itemId)} on stock</span></div>
    <div class="gl-cook-io"></div>
    <div class="gl-cook-qty">Qty
      <button class="gl-cook-minus">−</button>
      <input class="gl-cook-q" type="number" min="1" value="1" />
      <button class="gl-cook-plus">+</button>
      <span class="max">/ ${max} max</span>
    </div>
    <button class="gl-cook-go">Cook</button>`;

  const io = card.querySelector(".gl-cook-io")!;
  recipe.inputs.forEach((ing, i) => {
    if (i > 0) { const plus = document.createElement("span"); plus.className = "arrow"; plus.textContent = "+"; io.appendChild(plus); }
    const chip = document.createElement("div"); chip.className = "chip";
    chip.appendChild(iconFor(ing.itemId));
    const have = ownedCount(ing.itemId);
    const lab = document.createElement("span"); lab.className = have < ing.qty ? "lack" : "";
    lab.textContent = `${ITEM_NAMES[ing.itemId as keyof typeof ITEM_NAMES] ?? ing.itemId} ${have}/${ing.qty}`;
    chip.appendChild(lab); io.appendChild(chip);
  });
  const arrow = document.createElement("span"); arrow.className = "arrow"; arrow.textContent = "→"; io.appendChild(arrow);
  const outChip = document.createElement("div"); outChip.className = "chip";
  outChip.appendChild(iconFor(recipe.output.itemId));
  const outLab = document.createElement("span");
  outLab.textContent = ITEM_NAMES[recipe.output.itemId as keyof typeof ITEM_NAMES] ?? recipe.output.itemId;
  outChip.appendChild(outLab); io.appendChild(outChip);

  const input = card.querySelector<HTMLInputElement>(".gl-cook-q")!;
  const go = card.querySelector<HTMLButtonElement>(".gl-cook-go")!;
  const clamp = (): number => { let v = Math.floor(Number(input.value) || 1); v = Math.max(1, Math.min(Math.max(1, max), v)); input.value = String(v); return v; };
  card.querySelector(".gl-cook-minus")!.addEventListener("click", () => { input.value = String(Math.max(1, (Math.floor(Number(input.value) || 1)) - 1)); clamp(); });
  card.querySelector(".gl-cook-plus")!.addEventListener("click", () => { input.value = String((Math.floor(Number(input.value) || 1)) + 1); clamp(); });
  input.addEventListener("change", clamp);
  if (max < 1) { go.disabled = true; go.textContent = `Need ${recipe.inputs.map((g) => `${g.qty} ${ITEM_NAMES[g.itemId as keyof typeof ITEM_NAMES] ?? g.itemId}`).join(" + ")}`; }
  go.addEventListener("click", () => { if (go.disabled) return; const qty = clamp(); actions?.cook(recipe.id, qty); closeCook(); });
  return card;
}

function render(): void {
  if (!bodyEl) return;
  bodyEl.innerHTML = "";
  for (const recipe of COOK_RECIPES) bodyEl.appendChild(recipeCard(recipe));
  const close = document.createElement("button"); close.id = "gl-cook-close"; close.textContent = "Close (Esc)";
  close.addEventListener("click", closeCook); bodyEl.appendChild(close);
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) { const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s); }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-cook-overlay";
  overlay.innerHTML = `<div id="gl-cook-card">
    <div id="gl-cook-head"><h1>COOKING POT</h1><div class="hint">Pick a dish to cook. Stand still while the pot works.</div></div>
    <div id="gl-cook-body"></div>
  </div>`;
  document.body.appendChild(overlay);
  bodyEl = overlay.querySelector("#gl-cook-body");
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeCook(); });
}

export function openCook(): void {
  ensure();
  render();
  unsub = [inventoryStore.subscribe(render), foodMenuStore.subscribe(render)];
  overlay!.classList.add("open");
}
export function closeCook(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}
export function isCookOpen(): boolean { return overlay?.classList.contains("open") ?? false; }
